import { z } from "zod";

import { PDF_EXPORT_MODES } from "./pdf-exports";

// The **browser↔BFF** schemas for the public share pages (#1022) — the other
// seam from `@/server/data/internal-api/share-public`, which describes the
// BFF↔Python wire (`client/docs/architecture.md` L2, "Two seams, two homes for
// a schema").
//
// A file of its own, as `catalog-public.ts` is for the anonymous product page:
// the token-scoped surface should be readable in one sitting, and an input added
// beside the authed spec schemas would widen it by a diff nobody reads as
// widening it.

/**
 * The two ways a share link opens nothing (#551 D2), named by the landing each
 * one reaches. A tag never travels to the browser: the server function maps
 * #1019's two 403 tags onto these (`SHARE_DENIAL_BY_TAG`), so the page has a
 * landing to go to rather than an error to show.
 */
export const SHARE_DENIALS = ["archived", "link-invalid"] as const;

export type ShareDenial = (typeof SHARE_DENIALS)[number];

/**
 * Each share denial tag Python answers, by the denial it means. The literals are
 * #1019's (`SHARE_LINK_ARCHIVED_TAG`, `SHARE_LINK_INVALID_TAG` in the server-only
 * wire module, which this client-importable file cannot reach); a unit test
 * holds the two in step.
 */
export const SHARE_DENIAL_BY_TAG: Readonly<Record<string, ShareDenial>> = {
  "forbidden:share-link-archived": "archived",
  "forbidden:share-link-invalid": "link-invalid",
};

/**
 * The always-latest resolver's one input: the spec the link names and the
 * token it carries.
 *
 * Strict, so nothing but these two can be sent. The id is `.int().positive()`
 * because the route parses it out of the path itself and never asks for one
 * that cannot name a spec. The token is any string, empty included: an empty
 * one is answered `link-invalid` without a wire call, as modsy's 401 does — a
 * shape check here would turn that answer into a scrubbed validator error.
 */
export const resolveSharedLatestVersionInputSchema = z.strictObject({
  specificationId: z.number().int().positive(),
  shareToken: z.string(),
});

export type ResolveSharedLatestVersionInput = z.infer<typeof resolveSharedLatestVersionInputSchema>;

/**
 * Where a "Latest published" link lands: a version, or one of the two denials.
 * Never a thrown denial — the server function logs it server-side and answers
 * this, so neither 403 tag reaches the browser as an error.
 */
export type SharedLatestVersionOutcome =
  { outcome: "version"; versionId: number } | { outcome: ShareDenial };

// ---------------------------------------------------------------------------
// The pinned page's two reads (#1024)
// ---------------------------------------------------------------------------

/**
 * One version's summary, as a share link reads it: the spec, the version and
 * the token. Strict and positive for the resolver's reasons — the route parses
 * both ids out of the path and never asks for one that cannot name anything;
 * an empty token is a question with an answer.
 */
export const readSharedVersionSummaryInputSchema = z.strictObject({
  specificationId: z.number().int().positive(),
  versionId: z.number().int().positive(),
  shareToken: z.string(),
});

export type ReadSharedVersionSummaryInput = z.infer<typeof readSharedVersionSummaryInputSchema>;

/**
 * One page of that version's lines: the summary's three, plus the signed-in
 * lines page's paging (#910) — the cursor, every held line id, and under a
 * term the last page's group ids — and the term, sent only when there is one.
 */
export const readSharedVersionLinesInputSchema = readSharedVersionSummaryInputSchema.extend({
  cursor: z.number().int().nonnegative().optional(),
  search: z.string().optional(),
  heldLineIds: z.array(z.number().int()).optional(),
  snapshotGroupIds: z.array(z.number().int()).optional(),
});

export type ReadSharedVersionLinesInput = z.infer<typeof readSharedVersionLinesInputSchema>;

/**
 * The pinned page's search params: the token, and the signed-in details page's
 * own — `?specSearch=` (the line search) and `?lineId=&selectionId=` (the deep
 * link) — each the literal text the router's codec reads it as. **Every other
 * key passes through untouched**, so a link carrying one keeps it, as the
 * source's `URLSearchParams` does; and the route never rewrites the token,
 * which the codec writes back unencoded (`@/lib/router-search-codec`).
 */
export const publicSpecDetailsSearchSchema = z.looseObject({
  share_token: z.string().optional().catch(undefined),
  specSearch: z.string().optional().catch(undefined),
  lineId: z.string().optional().catch(undefined),
  selectionId: z.string().optional().catch(undefined),
});

export type PublicSpecDetailsSearch = z.infer<typeof publicSpecDetailsSearchSchema>;

// ---------------------------------------------------------------------------
// Public Print (#1025)
// ---------------------------------------------------------------------------

/**
 * Queue a print of the version on screen: the pinned page's three, plus the
 * document. An empty token is a question with an answer (`link-invalid`, no
 * wire call), as on the reads.
 */
export const createSharedPrintInputSchema = readSharedVersionSummaryInputSchema.extend({
  mode: z.enum(PDF_EXPORT_MODES),
});

export type CreateSharedPrintInput = z.infer<typeof createSharedPrintInputSchema>;

/** A link's print's status or bytes: the job, and the token that made it. */
export const readSharedPrintInputSchema = z.strictObject({
  jobId: z.uuid(),
  shareToken: z.string(),
});

export type ReadSharedPrintInput = z.infer<typeof readSharedPrintInputSchema>;
